import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Coordinates } from '../types';
import type { Road } from '@/types/road';
import {
  gpsDetectionService,
  GPSLocationSuggestion,
} from '../components/tasks/wizard/services/gpsDetectionService';
import { reverseGeocodingService } from '../components/tasks/wizard/services/reverseGeocodingService';
import { roadNameParser } from '../components/tasks/wizard/services/roadNameParser';
import { useWizardErrorHandler } from '../components/tasks/wizard/utils/errorHandling';

/**
 * Module-level stable empty roads list. Used as the default for the optional
 * `roads` option so its identity never changes across renders — otherwise a
 * fresh `[]` literal would re-create `detectLocation` (which lists `roads` in
 * its `useCallback` deps), re-running consumer detection effects every render.
 */
const EMPTY_ROADS: Road[] = [];

interface UseGPSLocationSuggestionOptions {
  /**
   * Optional client-side roads for proximity matching. Each {@link Road} must
   * carry real `geometry.coordinates`; roads without geometry can never match
   * (distance is `Infinity`) and detection falls through to reverse geocoding.
   * No geometry-backed list is supplied today, so detection currently resolves
   * via reverse geocoding only.
   */
  roads?: Road[];
  /** Maximum distance to consider a coordinate "on" a road (in meters) */
  maxRoadDistance?: number;
  /** Enable automatic detection on GPS location change */
  autoDetect?: boolean;
}

interface UseGPSLocationSuggestionReturn {
  /** Current suggestion from GPS location */
  suggestion: GPSLocationSuggestion | null;
  /** Whether detection is in progress */
  isDetecting: boolean;
  /** Error message if detection failed */
  error: string | null;
  /** Manually trigger detection for given coordinates */
  detectLocation: (coordinates: Coordinates) => Promise<void>;
  /** Clear current suggestion */
  clearSuggestion: () => void;
}

export function useGPSLocationSuggestion({
  roads = EMPTY_ROADS,
  maxRoadDistance = 100,
  autoDetect = true,
}: UseGPSLocationSuggestionOptions = {}): UseGPSLocationSuggestionReturn {
  const { t } = useTranslation();
  const handleError = useWizardErrorHandler('useGPSLocationSuggestion');
  const [suggestion, setSuggestion] = useState<GPSLocationSuggestion | null>(
    null
  );
  const [isDetecting, setIsDetecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const detectLocation = useCallback(
    async (coordinates: Coordinates): Promise<void> => {
      if (!autoDetect) return;

      try {
        setIsDetecting(true);
        setError(null);

        // Check cache first
        const cachedSuggestion =
          gpsDetectionService.getCachedSuggestion(coordinates);
        if (cachedSuggestion) {
          setSuggestion(cachedSuggestion);
          return;
        }

        // Step 1: Check if coordinates are near a predefined road
        const closestRoadResult = roadNameParser.findClosestRoadWithDistance(
          coordinates,
          roads
        );

        if (
          closestRoadResult &&
          closestRoadResult.distance <= maxRoadDistance
        ) {
          // We're on or near a predefined road
          // Use full road name directly to avoid intermediate "DW443" value in UI
          const roadName = closestRoadResult.road.name;
          const roadSuggestion = gpsDetectionService.createRoadSuggestion(
            roadName,
            closestRoadResult.distance
          );

          // Cache the result
          gpsDetectionService.cacheSuggestion(coordinates, roadSuggestion);

          setSuggestion(roadSuggestion);
          return;
        }

        // Step 2: Not near a predefined road, use reverse geocoding
        const addressString =
          await reverseGeocodingService.getAddress(coordinates);

        if (addressString) {
          const addressSuggestion =
            gpsDetectionService.createAddressSuggestion(addressString);

          // Cache the result
          gpsDetectionService.cacheSuggestion(coordinates, addressSuggestion);

          setSuggestion(addressSuggestion);
        } else {
          setSuggestion(null);
          setError(t('gps.noLocationFound'));
        }
      } catch (error) {
        const errorMsg = t('gps.detectionFailed');
        setError(errorMsg);
        setSuggestion(null);
        handleError(error, 'GPS location detection', { coordinates }, errorMsg);
      } finally {
        setIsDetecting(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [roads, maxRoadDistance, autoDetect, t]
  );
  // handleError removed from deps - used only in catch block, doesn't affect callback logic

  const clearSuggestion = useCallback(() => {
    setSuggestion(null);
    setError(null);
  }, []);

  return {
    suggestion,
    isDetecting,
    error,
    detectLocation,
    clearSuggestion,
  };
}
